import {
  Button,
  Checkbox,
  Input,
  Label,
  LoadingSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@/components/ui";
import { DataTableVisibilityDropdown } from "@/components/ui/shadcn/DataTable/DataTableVisibilityDropdown";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { dashboardRowsQueryOptions } from "@/lib/data/react-query/matters/dashboard-tables";
import type {
  DashboardActionCell,
  MatterDashboardTable,
} from "@/lib/types/MatterDashboardTable";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Search,
  UserRound,
} from "lucide-react";
import { useEffect, useId, useState } from "react";

import { DashboardClaimActionButton } from "./DashboardClaimActionButton";
import { DashboardMatterDataCell } from "./DashboardMatterDataCell";
import { DashboardMoreFilters } from "./DashboardMoreFilters";

const DEFAULT_PAGE_SIZE = 25;
const MAX_SAVED_VIEWS = 50;
const COLUMN_VISIBILITY_STORAGE_PREFIX = "lawworks:dashboard-table-columns:";

function readStoredColumnKeys(storageKey: string): string[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) &&
      parsed.every((key) => typeof key === "string")
      ? parsed
      : null;
  } catch {
    return null;
  }
}

type TableView = {
  search: string;
  page: number;
  pageSize: number;
  myTeams: boolean;
  massTortId: string | undefined;
  sortOrder: "asc" | "desc";
  visibleColumnKeys: string[] | null;
};

const savedViews = new Map<string, TableView>();

function rememberView(key: string, view: TableView) {
  savedViews.delete(key);
  savedViews.set(key, view);
  if (savedViews.size > MAX_SAVED_VIEWS) {
    const oldestKey = savedViews.keys().next().value;
    if (oldestKey) savedViews.delete(oldestKey);
  }
}

function ActionCell({
  cell,
  matterId,
  firmId,
  userId,
}: {
  cell: DashboardActionCell | null;
  matterId: string;
  firmId: string;
  userId: string;
}) {
  const { openTaskModal } = useGlobalModal();
  if (!cell) return <span className="text-muted-foreground">—</span>;
  const healthMessage =
    cell.kind !== "Evaluated" ||
    cell.evaluationHealth === "Current" ||
    cell.evaluationHealth === "Pending"
      ? null
      : cell.evaluationHealth === "Failed"
        ? "Evaluation failed"
        : "Evaluation unavailable";
  const statusTone = {
    Blocked: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200",
    Complete:
      "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200",
    Closed: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    Incomplete: "bg-muted text-muted-foreground",
    "In progress":
      "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200",
  } as const;
  const displayStatus =
    cell.effectiveStatus === "Incomplete" && cell.task?.status === "In Progress"
      ? "In progress"
      : cell.effectiveStatus;
  const isMyTask =
    cell.task?.assignees.some((assignee) => assignee.userId === userId) ??
    false;
  const canClaim =
    (cell.effectiveStatus === "Incomplete" ||
      cell.effectiveStatus === "Blocked") &&
    (!cell.task ||
      (cell.task.assignees.length === 0 &&
        (cell.task.status === "Open" || cell.task.status === "In Progress")));
  const assigneeNames = cell.task?.assignees
    .map(({ firstName, lastName, integrationAppId, integrationAppName }) =>
      integrationAppId
        ? `${integrationAppName ?? "Unavailable app"} (app)`
        : [firstName, lastName].filter(Boolean).join(" "),
    )
    .filter(Boolean)
    .join(", ");
  return (
    <div className="flex w-full min-w-0 flex-col items-start gap-1">
      <div className="flex items-center gap-1 whitespace-nowrap">
        <span
          className={`inline-flex shrink-0 rounded-md px-2 py-0.5 text-xs font-medium ${statusTone[displayStatus]}`}
        >
          {displayStatus}
        </span>
        {isMyTask && (
          <span className="opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
            <Button
              variant="outline"
              size="xs"
              className="h-6 shrink-0 rounded-md px-2 text-xs"
              onClick={() =>
                openTaskModal({
                  matterId,
                  taskId: cell.task?.id,
                  editing: true,
                  initialTab: "details",
                  hideMatterField: true,
                })
              }
            >
              View task
            </Button>
          </span>
        )}
        {canClaim && (
          <span className="opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
            <DashboardClaimActionButton
              action={cell}
              matterId={matterId}
              firmId={firmId}
              userId={userId}
              compact
            />
          </span>
        )}
      </div>
      {healthMessage && (
        <span
          className={`block text-xs ${cell.evaluationHealth === "Failed" ? "text-destructive" : "text-amber-700 dark:text-amber-300"}`}
          title="This action's current evaluation is not available."
        >
          {healthMessage}
        </span>
      )}
      {cell.task && (assigneeNames || displayStatus !== "Complete") && (
        <span
          className="text-muted-foreground flex w-full min-w-0 items-center gap-1 text-xs"
          title={assigneeNames || "Unassigned"}
        >
          <UserRound
            aria-hidden="true"
            className={`size-3 shrink-0 ${isMyTask ? "text-blue-600 dark:text-blue-400" : ""}`}
          />
          <span className="min-w-0 truncate">
            {assigneeNames || "Unassigned"}
          </span>
        </span>
      )}
    </div>
  );
}

export function CustomDashboardTable({
  table,
  firmId,
  userId,
  ready,
  afterSequence,
}: {
  table: MatterDashboardTable;
  firmId: string;
  userId: string;
  ready: boolean;
  afterSequence: number;
}) {
  const viewKey = JSON.stringify([firmId, userId, table.id]);
  const columnVisibilityStorageKey = `${COLUMN_VISIBILITY_STORAGE_PREFIX}${viewKey}`;
  const [initialView] = useState<TableView>(
    () =>
      savedViews.get(viewKey) ?? {
        search: "",
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        myTeams: false,
        massTortId: undefined,
        sortOrder: "asc",
        visibleColumnKeys: readStoredColumnKeys(columnVisibilityStorageKey),
      },
  );
  const [search, setSearch] = useState(initialView.search);
  const [page, setPage] = useState(initialView.page);
  const [pageSize, setPageSize] = useState(initialView.pageSize);
  const [myTeams, setMyTeams] = useState(initialView.myTeams);
  const [massTortId, setMassTortId] = useState(initialView.massTortId);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    initialView.sortOrder,
  );
  const myTeamsId = useId();
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[] | null>(
    initialView.visibleColumnKeys,
  );
  useEffect(() => {
    rememberView(viewKey, {
      search,
      page,
      pageSize,
      myTeams,
      massTortId,
      sortOrder,
      visibleColumnKeys,
    });
  }, [
    viewKey,
    search,
    page,
    pageSize,
    myTeams,
    massTortId,
    sortOrder,
    visibleColumnKeys,
  ]);
  useEffect(() => {
    try {
      if (visibleColumnKeys === null) {
        window.localStorage.removeItem(columnVisibilityStorageKey);
      } else {
        window.localStorage.setItem(
          columnVisibilityStorageKey,
          JSON.stringify(visibleColumnKeys),
        );
      }
    } catch {
      // Keep the current view usable when storage is unavailable.
    }
  }, [columnVisibilityStorageKey, visibleColumnKeys]);
  const debouncedSearch = useDebouncedValue(search, 300);
  const filters = {
    q: debouncedSearch.trim() || undefined,
    pageNumber: page,
    pageSize,
    myTeams,
    massTortId,
    sortOrder,
    afterSequence,
  };
  const { data, isPending, isError, isFetching, refetch } = useQuery({
    ...dashboardRowsQueryOptions(firmId, userId, table.id, filters),
    enabled: ready,
  });
  const payload = data?.data;
  const columnMetadata = new Map(
    [...(payload?.columns ?? []), ...(payload?.dataColumns ?? [])].map(
      (column) => [column.key, column] as const,
    ),
  );
  const columns = (payload?.columnOrder ?? [...columnMetadata.keys()])
    .map((key) => columnMetadata.get(key))
    .filter((column): column is NonNullable<typeof column> => !!column);
  const visibleColumns = columns.filter((column) =>
    visibleColumnKeys ? visibleColumnKeys.includes(column.key) : true,
  );
  const rows = payload?.rows ?? [];
  const total = data?.pagination?.count ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    if (!isPending && page > pageCount) setPage(pageCount);
  }, [isPending, page, pageCount]);
  const firstMatter = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastMatter = Math.min(page * pageSize, total);

  return (
    <section
      className="bg-card flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      aria-label={table.title}
    >
      <div className="shrink-0 px-4 py-3">
        <h3 className="text-base font-semibold">{table.title}</h3>
      </div>
      <div className="bg-muted/30 flex shrink-0 flex-wrap items-center justify-between gap-2 border-y px-4 py-3">
        <div className="relative w-full min-w-48 sm:w-80">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            aria-label={`Search matters in ${table.title}`}
            placeholder="Search matters"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            className="bg-card h-10 pl-9"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DashboardMoreFilters
            massTortId={massTortId}
            onMassTortChange={(next) => {
              setMassTortId(next);
              setPage(1);
            }}
          />
          <div className="flex items-center gap-2 px-2">
            <Checkbox
              id={myTeamsId}
              checked={myTeams}
              onCheckedChange={(checked) => {
                setMyTeams(checked === true);
                setPage(1);
              }}
            />
            <Label htmlFor={myTeamsId} className="cursor-pointer text-sm">
              Assigned to me or my teams
            </Label>
          </div>
          <DataTableVisibilityDropdown
            columns={columns.map((column) => ({
              id: column.key,
              label: column.title,
              isVisible: visibleColumnKeys
                ? visibleColumnKeys.includes(column.key)
                : true,
            }))}
            onVisibilityChange={(columnIds, visible) => {
              const changedIds = new Set(columnIds);
              const next = visible
                ? Array.from(
                    new Set([
                      ...visibleColumns.map((column) => column.key),
                      ...columnIds,
                    ]),
                  )
                : visibleColumns
                    .filter((column) => !changedIds.has(column.key))
                    .map((column) => column.key);
              setVisibleColumnKeys(
                visible && next.length === columns.length ? null : next,
              );
            }}
          />
        </div>
      </div>
      <Table
        containerClassName="min-h-0 min-w-0 flex-1 overscroll-contain"
        className="min-w-full table-fixed text-sm"
        style={{
          width:
            224 +
            visibleColumns.reduce(
              (width, column) =>
                width + (column.kind === "matterData" ? 224 : 176),
              0,
            ),
        }}
      >
        <TableHeader>
          <TableRow className="bg-muted hover:bg-muted">
            <TableHead
              aria-sort={sortOrder === "asc" ? "ascending" : "descending"}
              className="bg-muted after:bg-border sticky top-0 left-0 z-30 w-56 max-w-56 min-w-56 px-3 py-2 text-xs font-medium after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-px"
            >
              <button
                type="button"
                className="text-foreground hover:bg-muted/50 inline-flex max-w-full min-w-0 cursor-pointer items-center gap-1 rounded-sm px-1 py-0.5 text-left"
                aria-label="Sort by matter title"
                onClick={() => {
                  setSortOrder((value) => (value === "asc" ? "desc" : "asc"));
                  setPage(1);
                }}
              >
                <span>Matter</span>
                {sortOrder === "asc" ? (
                  <ArrowUp
                    className="text-foreground size-3.5 shrink-0"
                    aria-hidden
                  />
                ) : (
                  <ArrowDown
                    className="text-foreground size-3.5 shrink-0"
                    aria-hidden
                  />
                )}
              </button>
            </TableHead>
            {visibleColumns.map((column) => (
              <TableHead
                key={column.key}
                className={`bg-muted sticky top-0 z-20 px-3 py-2 text-xs font-medium whitespace-normal ${column.kind === "matterData" ? "w-56 max-w-56 min-w-56" : "w-44 max-w-44 min-w-44"}`}
              >
                {column.title}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isPending || (isFetching && !data) ? (
            <TableRow>
              <TableCell
                colSpan={visibleColumns.length + 1}
                className="h-28 text-center"
              >
                <span role="status" className="inline-flex items-center gap-2">
                  <LoadingSpinner />
                  Loading matters…
                </span>
              </TableCell>
            </TableRow>
          ) : isError ? (
            <TableRow>
              <TableCell
                colSpan={visibleColumns.length + 1}
                className="px-4 py-10 text-center text-sm"
              >
                Unable to load matters.{" "}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void refetch()}
                >
                  Try again
                </Button>
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={visibleColumns.length + 1}
                className="px-4 py-10 text-center text-sm"
              >
                No matters found.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.matterId}>
                <TableCell className="bg-card after:bg-border sticky left-0 z-10 w-56 max-w-56 min-w-56 px-3 py-3 align-top text-sm after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-px">
                  <Link
                    to="/matters/$matterId"
                    params={{ matterId: row.matterId }}
                    className="text-primary font-medium hover:underline"
                  >
                    {row.title?.trim() || "Untitled matter"}
                  </Link>
                  {row.matterStatusLabel && (
                    <span className="text-muted-foreground mt-1 block text-xs">
                      {row.matterStatusLabel}
                    </span>
                  )}
                </TableCell>
                {visibleColumns.map((column) => (
                  <TableCell
                    key={`${row.matterId}:${column.key}`}
                    className={`group px-3 py-3 align-top ${column.kind === "matterData" ? "w-56 max-w-56 min-w-56" : "w-44 max-w-44 min-w-44"}`}
                  >
                    {column.kind === "phaseAction" ? (
                      <ActionCell
                        cell={row.cells[column.key] ?? null}
                        matterId={row.matterId}
                        firmId={firmId}
                        userId={userId}
                      />
                    ) : (
                      <DashboardMatterDataCell
                        cell={row.dataCells?.[column.key]}
                        matterId={row.matterId}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {!isPending && !isError && (
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t px-4 py-2.5">
          <Text variant="subHeading">
            {total === 0
              ? "No matters"
              : total === 1
                ? "1 matter"
                : `Showing ${firstMatter} to ${lastMatter} of ${total} matters`}
          </Text>
          {pageCount > 1 && (
            <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
              <div className="flex items-center gap-2">
                <Text
                  as="span"
                  variant="subHeading"
                  className="whitespace-nowrap"
                >
                  Rows per page:
                </Text>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => {
                    setPageSize(Number(value));
                    setPage(1);
                  }}
                >
                  <SelectTrigger
                    className="h-8 w-[80px] shrink-0"
                    aria-label="Rows per page"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent side="top">
                    {[10, 25, 50, 100].map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  disabled={page <= 1}
                  onClick={() => {
                    setPage((value) => value - 1);
                  }}
                >
                  <span className="sr-only">Go to previous page</span>
                  <ChevronLeft />
                </Button>
                <Text as="span" variant="body" className="whitespace-nowrap">
                  Page {page} of {pageCount}
                </Text>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  disabled={page >= pageCount}
                  onClick={() => {
                    setPage((value) => value + 1);
                  }}
                >
                  <span className="sr-only">Go to next page</span>
                  <ChevronRight />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
